import { __awaiter, __generator } from "tslib";
import * as React from 'react';
import { compKey } from './key';
import { namespacedClassnames, FileUploadError } from '../../common';
import { Popover, Row, Icon, FileSelector, Tooltip, TinyLoader } from '../../primitives';
import { iconsMap } from '../../icons';
import { noop } from '@wix/answers-lib';
import { LibColor, Text } from '../../new-libby';
import { FloaterMenu, MenuItem, UploadErrorType } from '../../composites';
export var DEFAULT_MAX_SIZE_MB = 25;
export var ONE_MEGABYTE = 1000000;
var UNEXPECTED_ERROR = 'unexpectedError';
export var UploaderState;
(function (UploaderState) {
    UploaderState["UPLOAD_NEW"] = "upload-new-state";
    UploaderState["REPLACE"] = "replace-state";
    UploaderState["LOADING"] = "loading-state";
    UploaderState["ERROR"] = "error-state";
})(UploaderState || (UploaderState = {}));
var fileUploadErrorToErrorCode = function (e) {
    var _a;
    var _b, _c;
    var errorCode = (((_c = (_b = e.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.errorCode) || UNEXPECTED_ERROR);
    return (_a = {},
        _a[FileUploadError.MAX_FILE_SIZE_EXCEEDED] = UploadErrorType.MAX_SIZE,
        _a[FileUploadError.INVALID_EXTENSION] = UploadErrorType.FILE_TYPE,
        _a[UNEXPECTED_ERROR] = UploadErrorType.UNEXPECTED_ERROR,
        _a)[errorCode];
};
var sanitizeFileName = function (file) {
    var isSvgFile = file.type.includes('svg');
    var maybeCleanName = isSvgFile ? file.name.replace(/[()]/g, '') : file.name;
    return isSvgFile ? new File([file], maybeCleanName, { type: file.type }) : file;
};
var fileValidation = function (type, size, supportedFileTypes, maxSizeMb) {
    var typeError = supportedFileTypes.every(function (supportedType) { return !type.includes(supportedType.replace('.', '')); });
    var maxSizeBytes = (maxSizeMb || DEFAULT_MAX_SIZE_MB) * ONE_MEGABYTE;
    var maybeError = size > maxSizeBytes ? FileUploadError.MAX_FILE_SIZE_EXCEEDED : typeError ? FileUploadError.INVALID_EXTENSION : false;
    return maybeError ? { response: { data: { errorCode: maybeError } } } : false;
};
var useUploader = function (_a) {
    var imageUrl = _a.imageUrl, fileUploader = _a.fileUploader, maxSizeMb = _a.maxSizeMb;
    var fileToUploadRef = React.useRef(null);
    var _b = React.useState(null), fileToUpload = _b[0], setFileToUpload = _b[1];
    var _c = React.useState(''), error = _c[0], setError = _c[1];
    fileToUploadRef.current = fileToUpload;
    var uploaderState = React.useMemo(function () {
        if (fileToUpload)
            return UploaderState.LOADING;
        if (error)
            return UploaderState.ERROR;
        if (imageUrl)
            return UploaderState.REPLACE;
        return UploaderState.UPLOAD_NEW;
    }, [fileToUpload, error, imageUrl]);
    var resetUpload = function () { return setFileToUpload(null); };
    var resetError = function () { return setError(''); };
    var isLastUploadedFile = function (file) { var _a; return file.name === ((_a = fileToUploadRef.current) === null || _a === void 0 ? void 0 : _a.name); };
    var uploadFile = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var sanitizedFile, fileValidationError, uploadError, filelink, e_1, uploadError;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    resetError();
                    setFileToUpload(file);
                    sanitizedFile = sanitizeFileName(file);
                    fileValidationError = fileValidation(file.type, file.size, fileUploader.supportedFileTypes, maxSizeMb);
                    if (fileValidationError) {
                        uploadError = fileUploadErrorToErrorCode(fileValidationError);
                        setError(uploadError);
                        return [2 /*return*/, resetUpload()];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fileUploader.uploadFile({ file: sanitizedFile })];
                case 2:
                    filelink = (_a.sent()).filelink;
                    if (!isLastUploadedFile(sanitizedFile))
                        return [2 /*return*/];
                    resetUpload();
                    return [2 /*return*/, filelink];
                case 3:
                    e_1 = _a.sent();
                    uploadError = fileUploadErrorToErrorCode(e_1);
                    setError(uploadError);
                    resetUpload();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return {
        uploadFile: uploadFile,
        cancelUpload: resetUpload,
        resetError: resetError,
        uploaderState: uploaderState,
        error: error,
    };
};
var useOuterClick = function (onOuterClick) {
    var compRef = React.useRef(null);
    var _onOuterClick = function (event) {
        var _a;
        if (event.target && !((_a = compRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            onOuterClick();
        }
    };
    React.useEffect(function () {
        document.addEventListener('click', _onOuterClick);
        return function () { return document.removeEventListener('click', _onOuterClick); };
    }, []);
    return compRef;
};
export var CompactImageUploader = function (props) {
    var tp = function (key, params) { return props.t("common." + compKey + "." + key, params); };
    var _a = React.useState(false), hovered = _a[0], setHovered = _a[1];
    var _b = React.useState(false), popoverOpen = _b[0], setPopoverOpen = _b[1];
    var _c = useUploader({ imageUrl: props.imageUrl, fileUploader: props.fileUploader, maxSizeMb: props.maxSizeMb }), uploadFile = _c.uploadFile, cancelUpload = _c.cancelUpload, resetError = _c.resetError, uploaderState = _c.uploaderState, error = _c.error;
    var uploaderRef = useOuterClick(resetError);
    var onFileSelect = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var fileLink;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, uploadFile(file)];
                case 1:
                    fileLink = _a.sent();
                    if (fileLink) {
                        props.onChange(fileLink);
                    }
                    closePopover();
                    return [2 /*return*/];
            }
        });
    }); };
    var onCancel = function (e) {
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        cancelUpload();
    };
    var onRemoveImage = function () {
        props.onChange('');
        closePopover();
    };
    var mouseOver = function () { return setHovered(true); };
    var mouseLeave = function () { return setHovered(false); };
    var openPopover = function () { return setPopoverOpen(true); };
    var closePopover = function () { return setPopoverOpen(false); };
    var uploadImageIcon = React.createElement(Icon, { className: 'upload-new-icon', icon: iconsMap.plusWithBgBold });
    var replaceImageIcon = React.createElement(Icon, { className: 'replace-icon', icon: iconsMap.refreshWithBg });
    var errorIcon = React.createElement(Icon, { className: 'error-icon', icon: iconsMap.alertRoundFilled });
    var cancelIcon = React.createElement("div", { className: 'cancel-icon-wrapper', onClick: onCancel },
        React.createElement(Icon, { icon: iconsMap.xCircleBold, color: LibColor.red140 }));
    var loader = React.createElement(TinyLoader, null);
    var classNames = namespacedClassnames(compKey, props.className, uploaderState, { 'with-image': !!props.imageUrl, hovered: hovered });
    var getIconData = function () {
        var _a = props.texts, add = _a.add, cancel = _a.cancel, replace = _a.replace;
        var maybeReplaceNote = props.imageUrl ? '.replace' : '';
        var errorSize = props.texts.errorSize || tp("error." + UploadErrorType.MAX_SIZE + maybeReplaceNote, { limit: props.maxSizeMb || DEFAULT_MAX_SIZE_MB });
        var errorFileType = props.texts.errorFileType || tp("error." + UploadErrorType.FILE_TYPE + maybeReplaceNote, { allowedTypes: props.fileUploader.supportedFileTypes.join(', ') });
        switch (uploaderState) {
            case UploaderState.UPLOAD_NEW:
                return { icon: uploadImageIcon, tooltip: add };
            case UploaderState.REPLACE:
                return hovered || popoverOpen ? { icon: replaceImageIcon, tooltip: replace } : { icon: null, tooltip: '' };
            case UploaderState.LOADING:
                return { icon: hovered ? cancelIcon : loader, tooltip: cancel };
            case UploaderState.ERROR:
                var errorTooltip = error === UploadErrorType.FILE_TYPE ? errorFileType : errorSize;
                return { icon: errorIcon, tooltip: errorTooltip };
            default:
                return { icon: uploadImageIcon, tooltip: add };
        }
    };
    var wrapInPopoverTrigger = function (children) { return React.createElement("div", { className: 'replace-icon-wrapper', onClick: openPopover }, children); };
    var renderIconWithTooltip = function () {
        var tooltipHidden = (uploaderState === UploaderState.LOADING || uploaderState === UploaderState.REPLACE) && !hovered;
        var tooltipForceShown = uploaderState === UploaderState.ERROR;
        var iconData = getIconData();
        var isReplaceIcon = uploaderState === UploaderState.REPLACE;
        var iconWithTooltip = tooltipHidden ? iconData.icon : (React.createElement(Tooltip, { className: 'uploader-icon-tooltip', body: iconData.tooltip, relativeToBody: true, forceVisible: tooltipForceShown, align: 'left' }, iconData.icon));
        return isReplaceIcon ? wrapInPopoverTrigger(iconWithTooltip) : iconWithTooltip;
    };
    var popoverMenu = (React.createElement(FloaterMenu, null,
        React.createElement(MenuItem, { className: 'replace-image-menu-item', onSelect: noop, value: 'replace' },
            React.createElement(FileSelector, { className: 'file-selector-menu', supportedFileTypes: props.fileUploader.supportedFileTypes, onFileSelect: onFileSelect },
                React.createElement(Text, { type: 'hxs' }, tp('menu-option-replace')))),
        React.createElement(MenuItem, { className: 'remove-image-menu-item', onSelect: onRemoveImage, value: 'remove' },
            React.createElement(Text, { type: 'hxs', color: 'tr10' }, tp('menu-option-remove')))));
    var iconWrapper = (React.createElement(Row, { className: 'icon-wrapper', verticalAlignment: 'center', horizontalAlignment: 'center' }, renderIconWithTooltip()));
    var imageStyle = { backgroundImage: "url(\"" + props.imageUrl + "\")", backgroundSize: 'cover' };
    return (React.createElement("div", { ref: uploaderRef, className: classNames, "data-image-url": props.imageUrl, onMouseOver: mouseOver, onMouseLeave: mouseLeave, key: props.imageUrl },
        React.createElement("div", { className: 'image-layer', style: imageStyle }),
        uploaderState !== UploaderState.REPLACE ?
            React.createElement(FileSelector, { className: 'uploader-selector', supportedFileTypes: props.fileUploader.supportedFileTypes, onFileSelect: onFileSelect }, iconWrapper) :
            React.createElement(Popover, { className: 'uploader-popover-menu', body: popoverMenu, isOpen: popoverOpen, onOuterAction: closePopover }, iconWrapper)));
};
//# sourceMappingURL=index.js.map